import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SITE, ARIAKE } from "../lib/data";
import { Phone, Mail, MapPin, MessageCircle, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECTS = ["Ariake", "Castilla II", "Otro / no estoy seguro/a"];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project: "Ariake",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Por favor completa nombre, email y contacto.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "landing" });
      toast.success("¡Gracias! Hemos recibido tu información. Te contactamos pronto.");

      // Open WhatsApp with the pre-filled message
      const waMsg = encodeURIComponent(
        `Hola, soy ${form.name}. Estoy interesado/a en ${form.project}. Mi contacto es ${form.phone} y mi correo ${form.email}.${
          form.message ? ` Mensaje: ${form.message}` : ""
        }`
      );
      window.open(`https://wa.me/${SITE.whatsappIntl}?text=${waMsg}`, "_blank", "noopener");

      setForm({ name: "", email: "", phone: "", project: "Ariake", message: "" });
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "No se pudo enviar tu información. Intenta de nuevo o escríbenos por WhatsApp.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="bg-[hsl(var(--hh-bone))] py-24 md:py-32">
      <div className="container-hh grid grid-cols-12 gap-x-4 gap-y-10 md:gap-x-10 md:gap-y-12 lg:gap-16">
        <div className="col-span-12 md:col-span-5 reveal">
          <div className="flex items-center gap-4 mb-6">
            <span className="accent-line" />
            <span className="overline text-[hsl(var(--hh-moss))]">Contacto</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-balance">
            Tu nuevo hogar <em style={{ fontStyle: "italic", color: "hsl(var(--hh-moss))" }}>te espera.</em>
          </h2>
          <p className="mt-6 text-[hsl(0,0%,28%)] text-base md:text-lg leading-relaxed max-w-md">
            Déjanos tus datos y un asesor te contactará en menos de 24 horas.
            También puedes escribirnos directamente por WhatsApp.
          </p>

          <div className="mt-10 space-y-5 text-[hsl(0,0%,18%)]">
            <a href={`tel:${SITE.phoneIntl}`} className="flex items-start gap-4 group">
              <Phone size={18} className="mt-1 text-[hsl(var(--hh-moss))]" />
              <div>
                <div className="overline text-[hsl(var(--hh-moss))]" style={{ fontSize: "0.6rem" }}>Teléfono</div>
                <div className="font-display text-2xl group-hover:underline">{SITE.phone}</div>
              </div>
            </a>
            <a href={`mailto:${SITE.email}`} className="flex items-start gap-4 group">
              <Mail size={18} className="mt-1 text-[hsl(var(--hh-moss))]" />
              <div>
                <div className="overline text-[hsl(var(--hh-moss))]" style={{ fontSize: "0.6rem" }}>Correo</div>
                <div className="font-display text-xl md:text-2xl group-hover:underline break-all">{SITE.email}</div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <MapPin size={18} className="mt-1 text-[hsl(var(--hh-moss))]" />
              <div>
                <div className="overline text-[hsl(var(--hh-moss))]" style={{ fontSize: "0.6rem" }}>Ubicación</div>
                <div className="font-display text-xl md:text-2xl">{ARIAKE.location.address}</div>
                <div className="text-sm text-[hsl(0,0%,40%)]">{ARIAKE.location.city}</div>
              </div>
            </div>

            <a
              href={`https://wa.me/${SITE.whatsappIntl}?text=${encodeURIComponent("Hola, quiero información sobre Ariake.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 mt-4 btn-hh btn-hh-outline"
              data-testid="contact-whatsapp-cta"
            >
              <MessageCircle size={16} /> Hablar por WhatsApp
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="col-span-12 md:col-span-7 bg-white border border-black/10 p-8 md:p-12 reveal"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
            <div>
              <label className="overline text-[hsl(var(--hh-moss))]" style={{ fontSize: "0.62rem" }} htmlFor="name">
                Nombre completo *
              </label>
              <input
                id="name"
                name="name"
                value={form.name}
                onChange={onChange}
                className="field-line"
                required
                autoComplete="name"
                placeholder="Tu nombre"
                data-testid="contact-input-name"
              />
            </div>
            <div>
              <label className="overline text-[hsl(var(--hh-moss))]" style={{ fontSize: "0.62rem" }} htmlFor="phone">
                Contacto / teléfono *
              </label>
              <input
                id="phone"
                name="phone"
                value={form.phone}
                onChange={onChange}
                className="field-line"
                required
                autoComplete="tel"
                placeholder="+593 ..."
                data-testid="contact-input-phone"
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline text-[hsl(var(--hh-moss))]" style={{ fontSize: "0.62rem" }} htmlFor="email">
                Email *
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                className="field-line"
                required
                autoComplete="email"
                placeholder="tu@correo.com"
                data-testid="contact-input-email"
              />
            </div>
            <div className="md:col-span-2">
              <label className="overline text-[hsl(var(--hh-moss))]" style={{ fontSize: "0.62rem" }} htmlFor="project">
                Elige un proyecto
              </label>
              <select
                id="project"
                name="project"
                value={form.project}
                onChange={onChange}
                className="field-line bg-transparent"
                data-testid="contact-input-project"
              >
                {PROJECTS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="overline text-[hsl(var(--hh-moss))]" style={{ fontSize: "0.62rem" }} htmlFor="message">
                Déjanos un mensaje
              </label>
              <textarea
                id="message"
                name="message"
                value={form.message}
                onChange={onChange}
                className="field-line resize-none"
                rows={3}
                placeholder="Cuéntanos qué estás buscando…"
                data-testid="contact-input-message"
              />
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-[hsl(0,0%,40%)] max-w-sm">
              Al enviar aceptas ser contactado/a por un asesor House &amp; Home.
              Tus datos se usan solo con fines comerciales.
            </p>
            <button
              type="submit"
              disabled={loading}
              className="btn-hh btn-hh-primary disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {loading ? "Enviando..." : (
                <>
                  Enviar y abrir WhatsApp <ArrowRight size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
